import touristCenters from '../data/tourist_centers.json';
import './firebase.js';
import { initLanguage, getLanguage, setLanguage, t, updateDOMTranslations } from './i18n.js';
import { initMap, updateMapMarkers, updateMapTileLayer, flyToCenter, setUserLocation } from './map.js';
import { filterTouristCenters, extractProvincesAndCities } from './filter.js';
import { renderAffiliates, renderEmergency } from './affiliate.js';
import { getOperatingStatus, isBookmarked, toggleBookmark, addRecentView, getBookmarks } from './storage.js';
import { getTranslatedCenterName, getTranslatedAddress, getTranslatedLocationDesc, getTranslatedClosedDays, getTranslatedOperator, getTranslatedServices, PROVINCE_NAMES } from './translator.js';

import { PwaService } from '../infrastructure/services/PwaService.js';
import { SeoService } from '../infrastructure/services/SeoService.js';
import { GeoFaqGenerator } from '../domain/tourist-center/GeoFaqGenerator.js';
import { AffiliateService } from '../infrastructure/services/AffiliateService.js';
import { initScratchpadView, renderScratchpadView, getScratchpadInstance, saveScratchpadInstance } from './scratchpadView.js';

// Application State
const state = {
  centers: touristCenters,
  filteredCenters: touristCenters,
  selectedCenter: null,
  userLocation: null,
  activeTab: 'list', // 'list', 'deals', 'emergency', 'bookmarks', 'scratchpad'
  filters: {
    query: '',
    province: 'all',
    city: 'all',
    languages: { en: false, ja: false, zh: false },
    facilities: { luggage: false, wheelchair: false, wifi: false, interpretation: false, stamp: false },
    openOnly: false,
    bookmarkedOnly: false
  }
};

document.addEventListener('DOMContentLoaded', () => {
  initApp();
});

function initApp() {
  // 1. Initialize PWA
  PwaService.init();

  // 2. Initialize i18n
  initLanguage();

  // 3. Initialize Theme
  initTheme();

  // 4. Initialize Scratchpad Domain & View
  initScratchpadView(state.centers, (center) => {
    openDetailModal(center);
  });

  // 5. Populate Province Dropdowns
  populateProvinces();

  // 6. Initialize Map
  const map = initMap('map', 36.3, 127.8, 7);

  // 7. Setup Event Listeners
  setupEventListeners();

  // 8. Initial Filter & Render
  applyFilters();

  // 9. Render Partner & Emergency tabs
  renderAffiliates('affiliates-view-container');
  renderEmergency('emergency-view-container');

  // 10. Handle Deep Link Routing & Initialize SEO / JSON-LD
  handleDeepLinkRouting();

  // 11. Setup PWA Install Banner & Marketing Triggers
  setupPwaBanner();

  console.log('K-TourSpot initialized with 572 national tourist centers & Travel Scratchpad!');
}

function initTheme() {
  const savedTheme = localStorage.getItem('k_tourspot_theme') || 'light';
  document.documentElement.setAttribute('data-theme', savedTheme);
  updateThemeIcon(savedTheme);
}

function toggleTheme() {
  const current = document.documentElement.getAttribute('data-theme') || 'light';
  const next = current === 'dark' ? 'light' : 'dark';
  document.documentElement.setAttribute('data-theme', next);
  localStorage.setItem('k_tourspot_theme', next);
  updateThemeIcon(next);
}

function updateThemeIcon(theme) {
  const themeBtn = document.getElementById('btn-theme-toggle');
  if (themeBtn) {
    themeBtn.innerHTML = theme === 'dark' 
      ? '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="5"></circle><line x1="12" y1="1" x2="12" y2="3"></line><line x1="12" y1="21" x2="12" y2="23"></line><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line><line x1="1" y1="12" x2="3" y2="12"></line><line x1="21" y1="12" x2="23" y2="12"></line><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line></svg>'
      : '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path></svg>';
    themeBtn.title = theme === 'dark' ? t('themeToggleLight') : t('themeToggleDark');
  }
}

function populateProvinces() {
  const provinceSelect = document.getElementById('province-select');
  if (!provinceSelect) return;

  const currentLang = getLanguage();
  const data = extractProvincesAndCities(state.centers);
  let optionsHtml = `<option value="all">${t('allRegions')}</option>`;
  data.forEach(item => {
    let provDisplay = item.province;
    if (currentLang !== 'ko') {
      const provTrans = PROVINCE_NAMES[item.province];
      if (provTrans) {
        provDisplay = provTrans[currentLang] || provTrans.en || item.province;
      }
    }
    optionsHtml += `<option value="${item.province}" ${state.filters.province === item.province ? 'selected' : ''}>${provDisplay} (${item.cities.length || 1})</option>`;
  });
  provinceSelect.innerHTML = optionsHtml;
}

function setupEventListeners() {
  // Search input
  const searchInput = document.getElementById('search-input');
  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      state.filters.query = e.target.value;
      applyFilters();
    });
  }

  // Clear search button
  const clearBtn = document.getElementById('btn-clear-search');
  if (clearBtn && searchInput) {
    clearBtn.addEventListener('click', () => {
      searchInput.value = '';
      state.filters.query = '';
      applyFilters();
    });
  }

  // Province select
  const provinceSelect = document.getElementById('province-select');
  if (provinceSelect) {
    provinceSelect.addEventListener('change', (e) => {
      state.filters.province = e.target.value;
      applyFilters();
    });
  }

  // Quick Filter Chips Click
  const chips = document.querySelectorAll('.chip-filter');
  chips.forEach(chip => {
    chip.addEventListener('click', () => {
      const filterType = chip.dataset.filter;
      chip.classList.toggle('active');
      const isActive = chip.classList.contains('active');

      if (['en', 'ja', 'zh'].includes(filterType)) {
        state.filters.languages[filterType] = isActive;
      } else if (['luggage', 'wheelchair', 'wifi', 'interpretation', 'stamp'].includes(filterType)) {
        state.filters.facilities[filterType] = isActive;
      } else if (filterType === 'open') {
        state.filters.openOnly = isActive;
      } else if (filterType === 'bookmark') {
        state.filters.bookmarkedOnly = isActive;
      }

      applyFilters();
    });
  });

  // Quick Chips Scroll Nav Arrows & Drag & Mouse Wheel Support
  const chipsScroll = document.getElementById('quick-chips-scroll');
  const btnScrollLeft = document.getElementById('btn-chip-scroll-left');
  const btnScrollRight = document.getElementById('btn-chip-scroll-right');

  if (chipsScroll) {
    if (btnScrollLeft) {
      btnScrollLeft.addEventListener('click', () => {
        chipsScroll.scrollBy({ left: -160, behavior: 'smooth' });
      });
    }

    if (btnScrollRight) {
      btnScrollRight.addEventListener('click', () => {
        chipsScroll.scrollBy({ left: 160, behavior: 'smooth' });
      });
    }

    // Mouse wheel horizontal scroll
    chipsScroll.addEventListener('wheel', (e) => {
      if (e.deltaY !== 0) {
        e.preventDefault();
        chipsScroll.scrollLeft += e.deltaY;
      }
    }, { passive: false });

    // Mouse Drag to Scroll
    let isDown = false;
    let startX;
    let scrollLeft;

    chipsScroll.addEventListener('mousedown', (e) => {
      isDown = true;
      startX = e.pageX - chipsScroll.offsetLeft;
      scrollLeft = chipsScroll.scrollLeft;
    });

    chipsScroll.addEventListener('mouseleave', () => {
      isDown = false;
    });

    chipsScroll.addEventListener('mouseup', () => {
      isDown = false;
    });

    chipsScroll.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - chipsScroll.offsetLeft;
      const walk = (x - startX) * 1.5;
      chipsScroll.scrollLeft = scrollLeft - walk;
    });
  }

  // Language Selector
  const langSelect = document.getElementById('lang-select');
  if (langSelect) {
    langSelect.value = getLanguage();
    langSelect.addEventListener('change', (e) => {
      const newLang = e.target.value;
      setLanguage(newLang);
      applyFilters();
      renderAffiliates('affiliates-view-container');
      renderEmergency('emergency-view-container');
      renderBookmarksView();
      renderScratchpadView('scratchpad-view-container');
      populateProvinces();
      updateMapTileLayer(newLang);
      SeoService.updateSpotSeo(state.selectedCenter, newLang);

      // Re-render open modal if active
      const modal = document.getElementById('detail-modal');
      if (modal && modal.classList.contains('active') && state.selectedCenter) {
        openDetailModal(state.selectedCenter);
      }

      // Re-render Taxi modal or CheatSheet modal if active
      const taxiModal = document.getElementById('taxi-modal');
      if (taxiModal && taxiModal.classList.contains('active') && state.selectedCenter) {
        openTaxiModal(state.selectedCenter);
      }
      const cheatSheetModal = document.getElementById('cheatsheet-modal');
      if (cheatSheetModal && cheatSheetModal.classList.contains('active')) {
        openCheatSheetModal();
      }
      const pwaGuideModal = document.getElementById('pwa-guide-modal');
      if (pwaGuideModal && pwaGuideModal.classList.contains('active')) {
        openPwaGuideModal();
      }
      const stampModal = document.getElementById('stamp-modal');
      if (stampModal && stampModal.classList.contains('active')) {
        openStampModal();
      }

      // Update URL query
      const url = new URL(window.location);
      url.searchParams.set('lang', newLang);
      window.history.replaceState({}, '', url);
    });
  }

  // Handle Browser Back/Forward navigation (popstate)
  window.addEventListener('popstate', () => {
    handleDeepLinkRouting();
  });

  // Theme Toggle
  const themeBtn = document.getElementById('btn-theme-toggle');
  if (themeBtn) {
    themeBtn.addEventListener('click', toggleTheme);
  }

  // Find Near Me GPS Button
  const nearMeBtn = document.getElementById('btn-near-me');
  if (nearMeBtn) {
    nearMeBtn.addEventListener('click', requestUserLocation);
  }

  // Sidebar Tab Navigation
  const tabBtns = document.querySelectorAll('.tab-btn');
  tabBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const tab = btn.dataset.tab;
      setActiveTab(tab);
    });
  });

  // Mobile View Toggle
  const mobileMapBtn = document.getElementById('btn-mobile-map');
  const mobileListBtn = document.getElementById('btn-mobile-list');
  const sidebar = document.querySelector('.app-sidebar');

  if (mobileMapBtn && mobileListBtn && sidebar) {
    mobileMapBtn.addEventListener('click', () => {
      sidebar.classList.add('hidden-mobile');
      mobileMapBtn.classList.add('active');
      mobileListBtn.classList.remove('active');
    });

    mobileListBtn.addEventListener('click', () => {
      sidebar.classList.remove('hidden-mobile');
      mobileListBtn.classList.add('active');
      mobileMapBtn.classList.remove('active');
    });
  }

  // Modal Close
  const closeModalBtn = document.getElementById('btn-close-modal');
  const modalOverlay = document.getElementById('detail-modal');
  const handleModalClose = () => {
    modalOverlay.classList.remove('active');
    state.selectedCenter = null;
    const currentLang = getLanguage();
    SeoService.updateSpotSeo(null, currentLang);

    // Clean up spot parameter from URL without page reload
    const url = new URL(window.location);
    if (url.searchParams.has('spot')) {
      url.searchParams.delete('spot');
      window.history.pushState({}, '', url);
    }
  };

  if (closeModalBtn && modalOverlay) {
    closeModalBtn.addEventListener('click', handleModalClose);
    modalOverlay.addEventListener('click', (e) => {
      if (e.target === modalOverlay) {
        handleModalClose();
      }
    });
  }

  // Travel Survival Cheat Sheet Header Button
  const cheatSheetBtn = document.getElementById('btn-open-cheatsheet');
  if (cheatSheetBtn) {
    cheatSheetBtn.addEventListener('click', openCheatSheetModal);
  }

  // Close Taxi Modal
  const closeTaxiModalBtn = document.getElementById('btn-close-taxi-modal');
  const taxiModal = document.getElementById('taxi-modal');
  if (closeTaxiModalBtn && taxiModal) {
    closeTaxiModalBtn.addEventListener('click', () => taxiModal.classList.remove('active'));
    taxiModal.addEventListener('click', (e) => {
      if (e.target === taxiModal) taxiModal.classList.remove('active');
    });
  }

  // Close Cheatsheet Modal
  const closeCheatSheetModalBtn = document.getElementById('btn-close-cheatsheet-modal');
  const cheatSheetModal = document.getElementById('cheatsheet-modal');
  if (closeCheatSheetModalBtn && cheatSheetModal) {
    closeCheatSheetModalBtn.addEventListener('click', () => cheatSheetModal.classList.remove('active'));
    cheatSheetModal.addEventListener('click', (e) => {
      if (e.target === cheatSheetModal) cheatSheetModal.classList.remove('active');
    });
  }

  // Close PWA Guide Modal
  const closePwaGuideModalBtn = document.getElementById('btn-close-pwa-guide-modal');
  const pwaGuideModal = document.getElementById('pwa-guide-modal');
  if (closePwaGuideModalBtn && pwaGuideModal) {
    closePwaGuideModalBtn.addEventListener('click', () => pwaGuideModal.classList.remove('active'));
    pwaGuideModal.addEventListener('click', (e) => {
      if (e.target === pwaGuideModal) pwaGuideModal.classList.remove('active');
    });
  }

  // Close Stamp Guide Modal
  const closeStampModalBtn = document.getElementById('btn-close-stamp-modal');
  const stampModal = document.getElementById('stamp-modal');
  if (closeStampModalBtn && stampModal) {
    closeStampModalBtn.addEventListener('click', closeStampModal);
    stampModal.addEventListener('click', (e) => {
      if (e.target === stampModal) closeStampModal();
    });
  }

  // Global ESC key listener to close active modals
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (modalOverlay && modalOverlay.classList.contains('active')) handleModalClose();
      if (taxiModal && taxiModal.classList.contains('active')) taxiModal.classList.remove('active');
      if (cheatSheetModal && cheatSheetModal.classList.contains('active')) cheatSheetModal.classList.remove('active');
      if (pwaGuideModal && pwaGuideModal.classList.contains('active')) pwaGuideModal.classList.remove('active');
      if (stampModal && stampModal.classList.contains('active')) closeStampModal();
    }
  });

  // Delegate click for Stamp Tour Modal trigger & Map popup Taxi button
  document.addEventListener('click', (e) => {
    const stampTrigger = e.target.closest('[data-action="stamp-modal"], a[href="#stamp-modal"]');
    if (stampTrigger) {
      e.preventDefault();
      openStampModal();
      return;
    }

    const popupTaxiBtn = e.target.closest('.btn-popup-taxi');
    if (popupTaxiBtn) {
      const centerId = popupTaxiBtn.dataset.id;
      const targetCenter = state.centers.find(c => c.id === centerId);
      if (targetCenter) {
        openTaxiModal(targetCenter);
      }
    }
  });
}

function setActiveTab(tab) {
  state.activeTab = tab;
  
  // Update Tab Buttons
  document.querySelectorAll('.tab-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.tab === tab);
  });

  // Update View Panels
  document.querySelectorAll('.view-panel').forEach(panel => {
    panel.classList.toggle('active', panel.id === `view-${tab}`);
  });

  if (tab === 'bookmarks') {
    renderBookmarksView();
  } else if (tab === 'scratchpad') {
    renderScratchpadView('scratchpad-view-container');
  }
}

function requestUserLocation() {
  if (!navigator.geolocation) {
    alert('현재 브라우저에서 GPS 위치 서비스를 지원하지 않습니다.');
    return;
  }

  const nearMeBtn = document.getElementById('btn-near-me');
  if (nearMeBtn) nearMeBtn.innerHTML = '<span>📍 탐색중...</span>';

  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const { latitude, longitude } = pos.coords;
      state.userLocation = { lat: latitude, lng: longitude };
      setUserLocation(latitude, longitude);
      applyFilters();

      if (nearMeBtn) nearMeBtn.innerHTML = `<span>📍 내 주변 (${latitude.toFixed(2)}, ${longitude.toFixed(2)})</span>`;
    },
    (err) => {
      console.warn('Geolocation error:', err);
      // Fallback: Default to Seoul Station if blocked
      const fallbackLat = 37.5559;
      const fallbackLng = 126.9723;
      state.userLocation = { lat: fallbackLat, lng: fallbackLng };
      setUserLocation(fallbackLat, fallbackLng);
      applyFilters();
      if (nearMeBtn) nearMeBtn.innerHTML = `<span>📍 서울역 기준 탐색</span>`;
    }
  );
}

function applyFilters() {
  state.filteredCenters = filterTouristCenters(state.centers, {
    ...state.filters,
    userLocation: state.userLocation
  });

  renderCenterList();
  updateMapMarkers(state.filteredCenters, (center) => {
    openDetailModal(center);
  });
}

function renderCenterList() {
  const container = document.getElementById('centers-list-container');
  const countEl = document.getElementById('results-count-text');
  if (!container) return;

  const currentLang = getLanguage();
  const sp = getScratchpadInstance();

  if (countEl) {
    countEl.textContent = t('totalCount', { count: state.filteredCenters.length });
  }

  if (state.filteredCenters.length === 0) {
    container.innerHTML = `
      <div class="empty-state glass-panel" style="padding: 30px; text-align: center;">
        <p style="font-size: 1.5rem; margin-bottom: 8px;">🔍</p>
        <p style="color: var(--text-muted); font-size: 0.9rem;">${t('noResults')}</p>
        <button class="btn btn-secondary mt-8" id="btn-reset-filters">${t('resetFilter')}</button>
      </div>
    `;
    const resetBtn = document.getElementById('btn-reset-filters');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        resetAllFilters();
      });
    }
    return;
  }

  const cardsHtml = state.filteredCenters.slice(0, 100).map(center => {
    const op = getOperatingStatus(center);
    const bookmarked = isBookmarked(center.id);
    const inPad = sp.hasSpot(center.id);
    const displayName = getTranslatedCenterName(center, currentLang);
    const displayAddress = getTranslatedAddress(center, currentLang);

    const langBadges = [];
    if (center.languages.en) langBadges.push('<span class="badge badge-lang">EN</span>');
    if (center.languages.ja) langBadges.push('<span class="badge badge-lang">JA</span>');
    if (center.languages.zh) langBadges.push('<span class="badge badge-lang">ZH</span>');

    const facilityBadges = [];
    if (center.facilities.luggage) facilityBadges.push(`<span class="badge badge-facility">🧳 ${t('facLuggage')}</span>`);
    if (center.facilities.wheelchair) facilityBadges.push(`<span class="badge badge-facility">♿ ${t('facWheelchair')}</span>`);
    if (center.facilities.stamp) facilityBadges.push(`<span class="badge badge-facility">🏅 ${t('facStamp')}</span>`);

    const distText = center.distance !== undefined && center.distance !== null
      ? `<span class="card-distance">${t('distanceKm', { km: center.distance })}</span>`
      : '';

    return `
      <div class="center-card glass-panel" data-id="${center.id}">
        <div class="card-header">
          <span class="status-chip chip-${op.status}">${t(op.textKey)} (${op.hours})</span>
          ${distText}
        </div>
        <h3 class="card-title">${displayName}</h3>
        <p class="card-address">📍 ${displayAddress}</p>
        <div class="card-badges">
          ${langBadges.join('')}
          ${facilityBadges.join('')}
        </div>
        <div class="card-footer">
          <div class="card-actions">
            <button class="btn-card-action btn-view-map" data-id="${center.id}">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon><line x1="8" y1="2" x2="8" y2="18"></line><line x1="16" y1="6" x2="16" y2="22"></line></svg>
              <span>${t('viewOnMap')}</span>
            </button>
            <button class="btn-card-action btn-view-detail" data-id="${center.id}">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
              <span>${t('viewDetail')}</span>
            </button>
          </div>
          <div class="card-icon-btns">
            <button class="btn-card-taxi" data-id="${center.id}" title="${t('showToTaxi')}">
              🚖
            </button>
            <button class="btn-pad ${inPad ? 'active' : ''}" data-id="${center.id}" title="${inPad ? t('inScratchpad') : t('addToScratchpad')}">
              📝
            </button>
            <button class="btn-fav ${bookmarked ? 'active' : ''}" data-id="${center.id}" title="${t('bookmark')}">
              ${bookmarked ? '★' : '☆'}
            </button>
          </div>
        </div>
      </div>
    `;
  }).join('');

  container.innerHTML = cardsHtml;

  // Add click handlers on cards
  container.querySelectorAll('.center-card').forEach(card => {
    const id = card.dataset.id;
    const center = state.centers.find(c => c.id === id);

    card.addEventListener('click', (e) => {
      // Direct Taxi card click
      if (e.target.closest('.btn-card-taxi')) {
        openTaxiModal(center);
        return;
      }

      // Favorite click
      if (e.target.closest('.btn-fav')) {
        const favBtn = card.querySelector('.btn-fav');
        const isNowBookmarked = toggleBookmark(id);
        favBtn.classList.toggle('active', isNowBookmarked);
        favBtn.textContent = isNowBookmarked ? '★' : '☆';
        return;
      }

      // Scratchpad toggle click
      if (e.target.closest('.btn-pad')) {
        const padBtn = card.querySelector('.btn-pad');
        if (sp.hasSpot(id)) {
          sp.removeSpot(id);
          padBtn.classList.remove('active');
          padBtn.title = t('addToScratchpad');
        } else {
          sp.addSpot(id);
          padBtn.classList.add('active');
          padBtn.title = t('inScratchpad');
        }
        saveScratchpadInstance();
        return;
      }

      if (e.target.closest('.btn-view-map')) {
        flyToCenter(center);
        const sidebar = document.querySelector('.app-sidebar');
        if (sidebar && window.innerWidth <= 900) {
          sidebar.classList.add('hidden-mobile');
        }
        return;
      }

      openDetailModal(center);
    });
  });
}

function renderBookmarksView() {
  const container = document.getElementById('bookmarks-list-container');
  if (!container) return;

  const currentLang = getLanguage();
  const bookmarkedIds = getBookmarks();
  const bookmarkedCenters = state.centers.filter(c => bookmarkedIds.includes(c.id));
  const sp = getScratchpadInstance();

  if (bookmarkedCenters.length === 0) {
    container.innerHTML = `
      <div class="empty-state glass-panel" style="padding: 30px; text-align: center;">
        <p style="font-size: 1.5rem; margin-bottom: 8px;">★</p>
        <p style="color: var(--text-muted); font-size: 0.9rem;">아직 보관함에 저장된 관광안내소가 없습니다.<br>관광안내소 카드의 별표(★)를 눌러 저장해보세요!</p>
      </div>
    `;
    return;
  }

  const cardsHtml = bookmarkedCenters.map(center => {
    const op = getOperatingStatus(center);
    const inPad = sp.hasSpot(center.id);
    const displayName = getTranslatedCenterName(center, currentLang);
    const displayAddress = getTranslatedAddress(center, currentLang);

    return `
      <div class="center-card glass-panel" data-id="${center.id}">
        <div class="card-header">
          <span class="status-chip chip-${op.status}">${t(op.textKey)} (${op.hours})</span>
        </div>
        <h3 class="card-title">${displayName}</h3>
        <p class="card-address">📍 ${displayAddress}</p>
        <div class="card-footer">
          <button class="btn-card-action btn-view-detail" data-id="${center.id}">
            <span>${t('viewDetail')}</span>
          </button>
          <div class="card-icon-btns">
            <button class="btn-card-taxi" data-id="${center.id}" title="${t('showToTaxi')}">
              🚖
            </button>
            <button class="btn-pad ${inPad ? 'active' : ''}" data-id="${center.id}" title="${inPad ? t('inScratchpad') : t('addToScratchpad')}">
              📝
            </button>
            <button class="btn-fav active" data-id="${center.id}">★</button>
          </div>
        </div>
      </div>
    `;
  }).join('');

  container.innerHTML = cardsHtml;

  container.querySelectorAll('.center-card').forEach(card => {
    const id = card.dataset.id;
    const center = state.centers.find(c => c.id === id);

    card.addEventListener('click', (e) => {
      if (e.target.closest('.btn-card-taxi')) {
        openTaxiModal(center);
        return;
      }

      if (e.target.closest('.btn-fav')) {
        toggleBookmark(id);
        renderBookmarksView();
        renderCenterList();
        return;
      }

      if (e.target.closest('.btn-pad')) {
        const padBtn = card.querySelector('.btn-pad');
        if (sp.hasSpot(id)) {
          sp.removeSpot(id);
          padBtn.classList.remove('active');
        } else {
          sp.addSpot(id);
          padBtn.classList.add('active');
        }
        saveScratchpadInstance();
        renderBookmarksView();
        renderCenterList();
        return;
      }

      openDetailModal(center);
    });
  });
}

function openDetailModal(center) {
  if (!center) return;
  state.selectedCenter = center;
  addRecentView(center.id);

  const modal = document.getElementById('detail-modal');
  const titleEl = document.getElementById('modal-title');
  const bodyEl = document.getElementById('modal-body');
  if (!modal || !titleEl || !bodyEl) return;

  const currentLang = getLanguage();
  const op = getOperatingStatus(center);
  const bookmarked = isBookmarked(center.id);
  const sp = getScratchpadInstance();
  const inPad = sp.hasSpot(center.id);

  const displayName = getTranslatedCenterName(center, currentLang);
  const displayAddress = getTranslatedAddress(center, currentLang);
  const displayLocDesc = getTranslatedLocationDesc(center, currentLang);
  const displayClosed = getTranslatedClosedDays(center, currentLang);
  const displayOperator = getTranslatedOperator(center, currentLang);
  const displayServices = getTranslatedServices(center, currentLang);

  titleEl.textContent = displayName;

  const langList = [];
  if (center.languages.en) langList.push(currentLang === 'zh' ? '英语 (EN)' : currentLang === 'ja' ? '英語 (EN)' : currentLang === 'en' ? 'English (EN)' : '영어 (EN)');
  if (center.languages.ja) langList.push(currentLang === 'zh' ? '日语 (JA)' : currentLang === 'ja' ? '日本語 (JA)' : currentLang === 'en' ? 'Japanese (JA)' : '일본어 (JA)');
  if (center.languages.zh) langList.push(currentLang === 'zh' ? '中文 (ZH)' : currentLang === 'ja' ? '中国語 (ZH)' : currentLang === 'en' ? 'Chinese (ZH)' : '중국어 (ZH)');

  bodyEl.innerHTML = `
    <!-- Top Quick-Booking Hot Bar (Decision Fatigue Reducer & Direct Affiliate Link) -->
    <div class="modal-quick-deals-bar">
      <span class="quick-deals-label">⚡ ${currentLang === 'zh' ? '特惠快订' : currentLang === 'ja' ? 'スピード予約' : currentLang === 'en' ? 'Quick Book' : '특가 빠른예약'}:</span>
      <a href="${AffiliateService.getTrackedUrl('airalo', 'modal_top_esim')}" target="_blank" rel="noopener noreferrer" class="quick-deal-chip chip-esim" data-partner="airalo">
        📶 5G eSIM <span class="chip-save">20% OFF</span>
      </a>
      <a href="${AffiliateService.getTrackedUrl('kkday', 'modal_top_tours', center.province)}" target="_blank" rel="noopener noreferrer" class="quick-deal-chip chip-tour" data-partner="kkday">
        🎟️ ${currentLang === 'zh' ? '热门一日游' : currentLang === 'ja' ? '人気ツアー' : currentLang === 'en' ? 'Tours' : '주변 투어패스'}
      </a>
      <a href="${AffiliateService.getTrackedUrl('trip', 'modal_top_hotel', center.name)}" target="_blank" rel="noopener noreferrer" class="quick-deal-chip chip-hotel" data-partner="trip">
        🏨 ${currentLang === 'zh' ? '周边酒店' : currentLang === 'ja' ? '周辺ホテル' : currentLang === 'en' ? 'Hotels' : '인근 최저가 숙소'}
      </a>
    </div>

    <div class="detail-section">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; gap: 8px; flex-wrap: wrap;">
        <span class="status-chip chip-${op.status}">${t(op.textKey)} (${op.hours})</span>
        <div style="display: flex; gap: 6px; align-items: center; flex-wrap: wrap;">
          <button class="btn-taxi-trigger" id="modal-taxi-btn" title="${t('showToTaxi')}">
            ${t('showToTaxi')}
          </button>
          <button class="btn-share-trigger" id="modal-share-btn" title="${t('btnShare')}">
            ${t('btnShareCard')}
          </button>
          <button class="btn-pad-modal ${inPad ? 'active' : ''}" id="modal-pad-btn" data-id="${center.id}">
            ${inPad ? t('inScratchpad') : t('addToScratchpad')}
          </button>
          <button class="btn-fav ${bookmarked ? 'active' : ''}" id="modal-fav-btn" data-id="${center.id}">
            ${bookmarked ? '★ ' + t('bookmarked') : '☆ ' + t('bookmark')}
          </button>
        </div>
      </div>
      <p class="detail-text" style="font-weight: 600; color: var(--text-primary);">📍 ${displayAddress}</p>
      ${displayLocDesc ? `<p class="detail-text" style="color: var(--text-muted); font-size: 0.8rem;">(${displayLocDesc})</p>` : ''}
    </div>

    <div class="detail-section">
      <div class="detail-label">${t('summerHours')} & ${t('winterHours')}</div>
      <div class="detail-grid">
        <div class="detail-item-box">
          <div class="sub-label">☀️ ${t('summerHours')}</div>
          <div class="sub-value">${center.summerOpen} ~ ${center.summerClose}</div>
        </div>
        <div class="detail-item-box">
          <div class="sub-label">❄️ ${t('winterHours')}</div>
          <div class="sub-value">${center.winterOpen} ~ ${center.winterClose}</div>
        </div>
      </div>
      <div class="detail-item-box mt-8">
        <div class="sub-label">🗓️ ${t('closedDay')}</div>
        <div class="sub-value">${displayClosed}</div>
      </div>
    </div>

    <div class="detail-section">
      <div class="detail-label">${t('foreignLanguages')}</div>
      <p class="detail-text">${langList.length > 0 ? langList.join(', ') : (currentLang === 'zh' ? '韩语标准服务' : currentLang === 'ja' ? '韓国語基本案内' : currentLang === 'en' ? 'Korean Standard' : '한국어 기본 안내')}</p>
    </div>

    ${displayServices ? `
      <div class="detail-section">
        <div class="detail-label">${t('servicesProvided')}</div>
        <p class="detail-text">${displayServices}</p>
      </div>
    ` : ''}

    <div class="detail-section">
      <div class="detail-label">${t('operator')}</div>
      <p class="detail-text"><strong>${displayOperator}</strong></p>
      <p class="detail-text">${t('staffCount', { count: center.staffCount })}</p>
    </div>

    <div class="detail-section" style="border-bottom: none;">
      <div class="detail-label">${t('directions')} & ${t('callPhone')}</div>
      <div style="display: flex; flex-direction: column; gap: 8px; margin-top: 8px;">
        ${center.phone ? `
          <a href="tel:${center.phone}" class="btn btn-primary" style="width: 100%;">
            📞 ${t('callPhone')} (${center.phone})
          </a>
        ` : ''}
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(75px, 1fr)); gap: 8px;">
          <a href="https://map.kakao.com/link/to/${encodeURIComponent(center.name)},${center.lat},${center.lng}" target="_blank" rel="noopener" class="btn-nav-mini kakao" style="padding: 10px 0;">Kakao</a>
          <a href="https://map.naver.com/v5/search/${encodeURIComponent(center.name)}" target="_blank" rel="noopener" class="btn-nav-mini naver" style="padding: 10px 0;">Naver</a>
          <a href="https://www.google.com/maps/dir/?api=1&destination=${center.lat},${center.lng}" target="_blank" rel="noopener" class="btn-nav-mini google" style="padding: 10px 0;">Google</a>
          <a href="https://maps.apple.com/?daddr=${center.lat},${center.lng}&q=${encodeURIComponent(center.name)}" target="_blank" rel="noopener" class="btn-nav-mini apple" style="padding: 10px 0;">Apple</a>
        </div>
        ${center.homepage ? `
          <a href="${center.homepage}" target="_blank" rel="noopener" class="btn btn-secondary" style="width: 100%; margin-top: 4px;">
            🌐 ${t('website')}
          </a>
        ` : ''}
      </div>
    </div>

    <!-- Contextual Monetization Section (Travelpayouts Marker 764752) -->
    <div class="detail-section detail-monetization-card glass-panel" style="margin-top: 12px; padding: 14px; background: rgba(37, 99, 235, 0.04); border: 1px solid rgba(37, 99, 235, 0.2); border-radius: var(--radius-md);">
      <div class="detail-label" style="color: var(--brand-primary); font-weight: 700; margin-bottom: 8px;">
        ${t('specialDealsForSpot')}
      </div>
      <div class="spot-deals-grid">
        <a href="${AffiliateService.getTrackedUrl('tiqets', 'spot_modal_tiqets')}" target="_blank" rel="noopener noreferrer" class="btn-spot-deal" data-partner="tiqets">
          ${t('dealTiqets')} ➔
        </a>
        <a href="${AffiliateService.getTrackedUrl('airalo', 'spot_modal_esim')}" target="_blank" rel="noopener noreferrer" class="btn-spot-deal" data-partner="airalo">
          ${t('dealEsim')} ➔
        </a>
        <a href="${AffiliateService.getTrackedUrl('welcomepickups', 'spot_modal_pickup')}" target="_blank" rel="noopener noreferrer" class="btn-spot-deal" data-partner="welcomepickups">
          ${t('dealWelcomePickups')} ➔
        </a>
        <a href="${AffiliateService.getTrackedUrl('kiwi', 'spot_modal_flight')}" target="_blank" rel="noopener noreferrer" class="btn-spot-deal" data-partner="kiwi">
          ${t('dealKiwi')} ➔
        </a>
      </div>
    </div>

    <!-- Related Centers Section for Strong Internal Linking -->
    <div class="detail-section" style="padding-top: 14px;">
      <div class="detail-label">📍 ${currentLang === 'zh' ? '同地区其他推荐咨询处' : currentLang === 'ja' ? '同地域のその他おすすめ案内所' : currentLang === 'en' ? 'Nearby Centers in Same Region' : '동일 지역 인근 관광안내소'}</div>
      <div class="related-centers-grid">
        ${state.centers.filter(c => c.province === center.province && c.id !== center.id).slice(0, 4).map(rel => {
          const relName = getTranslatedCenterName(rel, currentLang);
          return `<button class="btn-related-center" data-id="${rel.id}">${relName}</button>`;
        }).join('')}
      </div>
    </div>

    <!-- GEO AI FAQ & E-E-A-T Section -->
    <div class="geo-faq-container">
      <div class="geo-faq-header">
        <span class="geo-faq-badge">AI GEO</span>
        <span class="geo-faq-title">${currentLang === 'zh' ? '常见问题 (FAQ)' : currentLang === 'ja' ? 'よくある質問 (FAQ)' : currentLang === 'en' ? 'Frequently Asked Questions (FAQ)' : '자주 묻는 질문 (FAQ)'}</span>
      </div>
      <div class="geo-faq-list">
        ${GeoFaqGenerator.generateFaq(center, currentLang).map(item => `
          <div class="geo-faq-item">
            <div class="geo-faq-q">${item.question}</div>
            <div class="geo-faq-a">${item.answer}</div>
          </div>
        `).join('')}
      </div>
      <div class="provenance-badge">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
        <span>${GeoFaqGenerator.generateProvenance(center, currentLang)}</span>
      </div>
    </div>
  `;

  modal.classList.add('active');

  // Bind Related Centers clicks
  bodyEl.querySelectorAll('.btn-related-center').forEach(btn => {
    btn.addEventListener('click', () => {
      const relId = btn.dataset.id;
      const targetCenter = state.centers.find(c => c.id === relId);
      if (targetCenter) {
        openDetailModal(targetCenter);
        flyToCenter(targetCenter);
      }
    });
  });

  // Update SEO & JSON-LD
  SeoService.updateSpotSeo(center, currentLang);

  // Update browser URL (Deep link Permalink)
  const url = new URL(window.location);
  url.searchParams.set('spot', center.name);
  url.searchParams.set('lang', currentLang);
  window.history.pushState({ spot: center.name }, '', url);

  const modalFavBtn = document.getElementById('modal-fav-btn');
  if (modalFavBtn) {
    modalFavBtn.addEventListener('click', () => {
      const isNow = toggleBookmark(center.id);
      modalFavBtn.classList.toggle('active', isNow);
      modalFavBtn.textContent = isNow ? '★ ' + t('bookmarked') : '☆ ' + t('bookmark');
      renderCenterList();
    });
  }

  const modalPadBtn = document.getElementById('modal-pad-btn');
  if (modalPadBtn) {
    modalPadBtn.addEventListener('click', () => {
      if (sp.hasSpot(center.id)) {
        sp.removeSpot(center.id);
        modalPadBtn.classList.remove('active');
        modalPadBtn.textContent = t('addToScratchpad');
      } else {
        sp.addSpot(center.id);
        modalPadBtn.classList.add('active');
        modalPadBtn.textContent = t('inScratchpad');
      }
      saveScratchpadInstance();
      renderCenterList();
      if (state.activeTab === 'scratchpad') {
        renderScratchpadView('scratchpad-view-container');
      }
    });
  }

  // Taxi Card & Social Share buttons
  const modalTaxiBtn = document.getElementById('modal-taxi-btn');
  if (modalTaxiBtn) {
    modalTaxiBtn.addEventListener('click', () => {
      openTaxiModal(center);
    });
  }

  const modalShareBtn = document.getElementById('modal-share-btn');
  if (modalShareBtn) {
    modalShareBtn.addEventListener('click', () => {
      shareSpot(center);
    });
  }
}

function handleDeepLinkRouting() {
  const params = new URLSearchParams(window.location.search);
  const langParam = params.get('lang');
  const spotParam = params.get('spot');

  if (langParam && ['ko', 'en', 'ja', 'zh'].includes(langParam)) {
    setLanguage(langParam);
    const langSelect = document.getElementById('lang-select');
    if (langSelect) langSelect.value = langParam;
    updateMapTileLayer(langParam);
  }

  const currentLang = getLanguage();

  if (spotParam) {
    const decodedSpot = decodeURIComponent(spotParam);
    const found = state.centers.find(c => c.name === decodedSpot || c.id === decodedSpot || c.translatedName?.en === decodedSpot);
    if (found) {
      openDetailModal(found);
      flyToCenter(found.lat, found.lng, 15);
      return;
    }
  }

  // Handle #stamp-modal hash
  if (window.location.hash === '#stamp-modal') {
    openStampModal();
  }

  // Home SEO
  SeoService.updateSpotSeo(null, currentLang);
}

function resetAllFilters() {
  state.filters = {
    query: '',
    province: 'all',
    city: 'all',
    languages: { en: false, ja: false, zh: false },
    facilities: { luggage: false, wheelchair: false, wifi: false, interpretation: false, stamp: false },
    openOnly: false,
    bookmarkedOnly: false
  };

  const searchInput = document.getElementById('search-input');
  if (searchInput) searchInput.value = '';

  const provinceSelect = document.getElementById('province-select');
  if (provinceSelect) provinceSelect.value = 'all';

  document.querySelectorAll('.chip-filter').forEach(c => c.classList.remove('active'));

  applyFilters();
}

/**
 * Open Show to Taxi Driver Modal (Large Destination Card for Foreigners)
 */
function openTaxiModal(center) {
  if (!center) return;
  const modal = document.getElementById('taxi-modal');
  const body = document.getElementById('taxi-modal-body');
  if (!modal || !body) return;

  body.innerHTML = `
    <div class="taxi-driver-banner">
      <div class="taxi-driver-title">🚕 ${t('taxiCardHeader')}</div>
      <div class="taxi-driver-subtitle">${t('taxiCardSubHeader')}</div>
    </div>

    <div class="taxi-info-block destination-block">
      <div class="taxi-field-label"><span class="taxi-badge-pill blue">📍 ${t('taxiCardDestination')}</span></div>
      <div class="taxi-destination-name">${center.name}</div>
    </div>

    <div class="taxi-info-block address-block">
      <div class="taxi-field-label"><span class="taxi-badge-pill">🗺️ ${t('taxiCardAddress')}</span></div>
      <div class="taxi-address-text">${center.address}</div>
      ${center.locationDesc ? `<div class="taxi-address-sub">(${center.locationDesc})</div>` : ''}
    </div>

    ${center.phone ? `
      <div class="taxi-info-block phone-block">
        <div class="taxi-field-label"><span class="taxi-badge-pill green">📞 ${t('taxiCardPhone')}</span></div>
        <div class="taxi-phone-text">📞 ${center.phone}</div>
      </div>
    ` : ''}

    <div class="taxi-actions-row">
      <button class="btn-taxi-action copy" id="btn-taxi-copy-addr">
        📋 ${t('btnCopyAddress')}
      </button>
      ${center.phone ? `
        <a href="tel:${center.phone}" class="btn-taxi-action call">
          📞 ${t('callPhone')}
        </a>
      ` : ''}
    </div>
  `;

  modal.classList.add('active');

  const copyBtn = document.getElementById('btn-taxi-copy-addr');
  if (copyBtn) {
    copyBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(`${center.name} (${center.address})`);
        copyBtn.textContent = '✓ ' + t('copied');
        setTimeout(() => {
          copyBtn.textContent = '📋 ' + t('btnCopyAddress');
        }, 2000);
      } catch (err) {
        console.error('Clipboard copy failed:', err);
      }
    });
  }
}

/**
 * Open K-Travel Survival Cheat Sheet Modal (4-Language Guide)
 */
function openCheatSheetModal() {
  const modal = document.getElementById('cheatsheet-modal');
  const body = document.getElementById('cheatsheet-modal-body');
  if (!modal || !body) return;

  body.innerHTML = `
    <div class="cheat-card">
      <div class="cheat-card-title">${t('csTransitTitle')}</div>
      <div class="cheat-card-desc">${t('csTransitDesc')}</div>
    </div>
    <div class="cheat-card">
      <div class="cheat-card-title">${t('csTaxTitle')}</div>
      <div class="cheat-card-desc">${t('csTaxDesc')}</div>
    </div>
    <div class="cheat-card">
      <div class="cheat-card-title">${t('csLuggageTitle')}</div>
      <div class="cheat-card-desc">${t('csLuggageDesc')}</div>
    </div>
    <div class="cheat-card">
      <div class="cheat-card-title">${t('csTippingTitle')}</div>
      <div class="cheat-card-desc">${t('csTippingDesc')}</div>
    </div>
    <div class="cheat-card">
      <div class="cheat-card-title">${t('csHotlineTitle')}</div>
      <div class="cheat-card-desc">${t('csHotlineDesc')}</div>
    </div>
  `;

  modal.classList.add('active');
}

/**
 * Open Stamp Tour Guide & Rewards Modal (4-Language Guide with 1-Click Filter)
 */
function openStampModal() {
  const modal = document.getElementById('stamp-modal');
  const body = document.getElementById('stamp-modal-body');
  if (!modal || !body) return;

  const currentLang = getLanguage();
  const stampCentersCount = state.centers.filter(c => c.facilities && c.facilities.stamp).length;

  body.innerHTML = `
    <!-- 3-Step Guide Section -->
    <div class="stamp-section-card">
      <div class="stamp-section-title">${t('stampStepsSectionTitle')}</div>
      <div class="stamp-steps-list">
        <div class="stamp-step-card">
          <div class="stamp-step-badge">1</div>
          <div class="stamp-step-body">
            <div class="stamp-step-title">${t('stampStep1Title')}</div>
            <div class="stamp-step-desc">${t('stampStep1Desc')}</div>
          </div>
        </div>
        <div class="stamp-step-card">
          <div class="stamp-step-badge">2</div>
          <div class="stamp-step-body">
            <div class="stamp-step-title">${t('stampStep2Title')}</div>
            <div class="stamp-step-desc">${t('stampStep2Desc')}</div>
          </div>
        </div>
        <div class="stamp-step-card">
          <div class="stamp-step-badge">3</div>
          <div class="stamp-step-body">
            <div class="stamp-step-title">${t('stampStep3Title')}</div>
            <div class="stamp-step-desc">${t('stampStep3Desc')}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Rewards Grid Section -->
    <div class="stamp-section-card">
      <div class="stamp-section-title">${t('stampRewardsSectionTitle')}</div>
      <div class="stamp-rewards-grid">
        <div class="stamp-reward-item">
          <div class="stamp-reward-header">${t('stampRewardGiftCard')}</div>
          <div class="stamp-reward-desc">${t('stampRewardGiftCardDesc')}</div>
        </div>
        <div class="stamp-reward-item">
          <div class="stamp-reward-header">${t('stampRewardGoods')}</div>
          <div class="stamp-reward-desc">${t('stampRewardGoodsDesc')}</div>
        </div>
        <div class="stamp-reward-item">
          <div class="stamp-reward-header">${t('stampRewardFood')}</div>
          <div class="stamp-reward-desc">${t('stampRewardFoodDesc')}</div>
        </div>
        <div class="stamp-reward-item">
          <div class="stamp-reward-header">${t('stampRewardCert')}</div>
          <div class="stamp-reward-desc">${t('stampRewardCertDesc')}</div>
        </div>
      </div>
    </div>

    <!-- Mobile Stamp Apps Section -->
    <div class="stamp-section-card">
      <div class="stamp-section-title">${t('stampAppsSectionTitle')}</div>
      <div class="stamp-app-tags">
        <span class="stamp-app-tag">📱 올댓스탬프 (All That Stamp)</span>
        <span class="stamp-app-tag">🗺️ 스탬프투어 (ThatsIt)</span>
        <span class="stamp-app-tag">🚶 워크온 (WalkOn)</span>
      </div>
    </div>

    <!-- Filter Action Button -->
    <div style="margin-top: 4px;">
      <button id="btn-filter-stamp-action" class="btn-filter-stamp-centers">
        ${t('btnFilterStampCenters', { count: stampCentersCount })}
      </button>
    </div>
  `;

  modal.classList.add('active');

  // Bind filter button click
  const filterBtn = document.getElementById('btn-filter-stamp-action');
  if (filterBtn) {
    filterBtn.addEventListener('click', () => {
      closeStampModal();
      setActiveTab('list');

      // Activate stamp quick filter chip
      document.querySelectorAll('.chip-filter').forEach(chip => {
        if (chip.dataset.filter === 'stamp') {
          chip.classList.add('active');
        }
      });
      state.filters.facilities.stamp = true;
      applyFilters();

      // If mobile, show list view
      const sidebar = document.querySelector('.app-sidebar');
      const mobileListBtn = document.getElementById('btn-mobile-list');
      const mobileMapBtn = document.getElementById('btn-mobile-map');
      if (sidebar && mobileListBtn && mobileMapBtn && window.innerWidth <= 900) {
        sidebar.classList.remove('hidden-mobile');
        mobileListBtn.classList.add('active');
        mobileMapBtn.classList.remove('active');
      }
    });
  }
}

function closeStampModal() {
  const modal = document.getElementById('stamp-modal');
  if (modal) {
    modal.classList.remove('active');
    if (window.location.hash === '#stamp-modal') {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }
}

/**
 * Viral Social Share Engine (Web Share API with Clipboard Fallback)
 */
async function shareSpot(center) {
  if (!center) return;
  const currentLang = getLanguage();
  const displayName = getTranslatedCenterName(center, currentLang);
  const displayAddress = getTranslatedAddress(center, currentLang);
  const shareUrl = `https://k-tour.web.app/?spot=${encodeURIComponent(center.name)}&lang=${currentLang}`;
  const shareData = {
    title: `[K-TourSpot] ${displayName}`,
    text: `🇰🇷 ${displayName}\n📍 ${displayAddress}\n\nK-TourSpot에서 전국 572개 관광안내소 실시간 운영정보 & 짐보관/통역 혜택을 확인하세요!`,
    url: shareUrl
  };

  if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
    try {
      await navigator.share(shareData);
      return;
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.warn('Navigator share error:', err);
      }
    }
  }

  // Fallback to clipboard
  try {
    await navigator.clipboard.writeText(`${shareData.text}\n${shareUrl}`);
    alert(t('sharedSuccess'));
  } catch (err) {
    console.error('Clipboard copy failed:', err);
  }
}

/**
 * PWA Install Floating Banner Manager & Universal Guide Trigger
 */
function setupPwaBanner() {
  const banner = document.getElementById('pwa-install-banner');
  const installBtn = document.getElementById('btn-pwa-install-action');
  const dismissBtn = document.getElementById('btn-pwa-dismiss');
  if (!banner) return;

  // If already installed in standalone mode, do not show banner
  if (PwaService.isInstalled) return;

  const isDismissed = sessionStorage.getItem('k_pwa_banner_dismissed');
  if (!isDismissed) {
    setTimeout(() => {
      banner.classList.add('show');
    }, 4000);
  }

  if (installBtn) {
    installBtn.addEventListener('click', async () => {
      banner.classList.remove('show');
      sessionStorage.setItem('k_pwa_banner_dismissed', 'true');

      if (PwaService.hasPrompt()) {
        const accepted = await PwaService.promptInstall();
        if (!accepted) {
          openPwaGuideModal();
        }
      } else {
        openPwaGuideModal();
      }
    });
  }

  if (dismissBtn) {
    dismissBtn.addEventListener('click', () => {
      banner.classList.remove('show');
      sessionStorage.setItem('k_pwa_banner_dismissed', 'true');
    });
  }
}

/**
 * Open PWA Step-by-Step Installation Guide Modal (iOS, Android, Desktop)
 */
function openPwaGuideModal() {
  const modal = document.getElementById('pwa-guide-modal');
  const body = document.getElementById('pwa-guide-modal-body');
  if (!modal || !body) return;

  const ua = (navigator.userAgent || '').toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua);
  const isAndroid = /android/.test(ua);

  body.innerHTML = `
    <!-- iOS Guide -->
    <div class="pwa-platform-guide ${isIOS ? 'active-platform' : ''}">
      <div class="platform-guide-header">
        <span class="platform-icon">🍎</span>
        <span class="platform-title">${t('pwaStepIos')}</span>
        ${isIOS ? '<span class="current-device-badge">내 기기 (Detected)</span>' : ''}
      </div>
      <div class="platform-guide-steps">
        <div class="guide-step-item">
          <div class="step-num">1</div>
          <div class="step-desc">사파리(Safari) 화면 하단의 <strong>[공유 버튼 ⎘]</strong>을 탭합니다.</div>
        </div>
        <div class="guide-step-item">
          <div class="step-num">2</div>
          <div class="step-desc">아래로 스크롤하여 <strong>[홈 화면에 추가 ➕]</strong>를 선택합니다.</div>
        </div>
        <div class="guide-step-item">
          <div class="step-num">3</div>
          <div class="step-desc">우측 상단의 <strong>[추가]</strong>를 누르면 바탕화면에 앱이 생성됩니다!</div>
        </div>
      </div>
    </div>

    <!-- Android Guide -->
    <div class="pwa-platform-guide ${isAndroid ? 'active-platform' : ''}">
      <div class="platform-guide-header">
        <span class="platform-icon">🤖</span>
        <span class="platform-title">${t('pwaStepAndroid')}</span>
        ${isAndroid ? '<span class="current-device-badge">내 기기 (Detected)</span>' : ''}
      </div>
      <div class="platform-guide-steps">
        <div class="guide-step-item">
          <div class="step-num">1</div>
          <div class="step-desc">브라우저 우측 상단의 <strong>[더보기 메뉴 ⋮]</strong>를 탭합니다.</div>
        </div>
        <div class="guide-step-item">
          <div class="step-num">2</div>
          <div class="step-desc"><strong>[앱 설치]</strong> 또는 <strong>[홈 화면에 추가]</strong>를 누릅니다.</div>
        </div>
        <div class="guide-step-item">
          <div class="step-num">3</div>
          <div class="step-desc">설치 확인을 누르면 바탕화면에 즉시 앱이 생성됩니다!</div>
        </div>
      </div>
    </div>

    <!-- PC/Mac Desktop Guide -->
    <div class="pwa-platform-guide ${!isIOS && !isAndroid ? 'active-platform' : ''}">
      <div class="platform-guide-header">
        <span class="platform-icon">💻</span>
        <span class="platform-title">${t('pwaStepPc')}</span>
        ${!isIOS && !isAndroid ? '<span class="current-device-badge">내 기기 (Detected)</span>' : ''}
      </div>
      <div class="platform-guide-steps">
        <div class="guide-step-item">
          <div class="step-num">1</div>
          <div class="step-desc">주소창(URL) 맨 우측의 <strong>[설치 📥]</strong> 아이콘을 클릭합니다.</div>
        </div>
        <div class="guide-step-item">
          <div class="step-num">2</div>
          <div class="step-desc"><strong>[설치]</strong> 버튼을 누르면 독립 앱 창으로 바로 실행됩니다!</div>
        </div>
      </div>
    </div>

    <div style="margin-top: 14px; text-align: center;">
      <button class="btn btn-primary" id="btn-close-pwa-guide-action" style="width: 100%; padding: 13px; font-weight: 800; font-size: 1rem; border-radius: 12px;">
        ✓ ${t('btnGotIt')}
      </button>
    </div>
  `;

  modal.classList.add('active');

  const closeActionBtn = document.getElementById('btn-close-pwa-guide-action');
  if (closeActionBtn) {
    closeActionBtn.addEventListener('click', () => {
      modal.classList.remove('active');
    });
  }
}

